import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { VerdictBadge, TierBadge, StatusText, RiskDot } from "@/components/Badges";
import { Search } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function CaseHistory() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [verdict, setVerdict] = useState("ALL");
  const [tier, setTier] = useState("ALL");

  const load = () => {
    const params = {};
    if (q) params.q = q;
    if (verdict !== "ALL") params.verdict = verdict;
    if (tier !== "ALL") params.tier = tier;
    api.get("/screenings", { params }).then((r) => setRows(r.data)).catch(() => {});
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [verdict, tier]);

  const input =
    "rounded-lg border border-slate-700 bg-slate-950 px-3 py-2.5 text-sm text-slate-100 outline-none focus:border-blue-500";

  return (
    <div className="space-y-6" data-testid="case-history-page">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-100 sm:text-3xl">
          Case History
        </h1>
        <p className="mt-1 text-sm text-slate-400">Search and filter all screenings.</p>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <input
            data-testid="case-search-input"
            className={`${input} w-full pl-9`}
            placeholder="Search by entity name…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
          />
        </div>
        <Select value={verdict} onValueChange={setVerdict}>
          <SelectTrigger data-testid="filter-verdict" className={`${input} w-40`}>
            <SelectValue placeholder="Verdict" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All verdicts</SelectItem>
            <SelectItem value="APPROVE">APPROVE</SelectItem>
            <SelectItem value="REVIEW">REVIEW</SelectItem>
            <SelectItem value="REJECT">REJECT</SelectItem>
          </SelectContent>
        </Select>
        <Select value={tier} onValueChange={setTier}>
          <SelectTrigger data-testid="filter-tier" className={`${input} w-52`}>
            <SelectValue placeholder="Tier" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All tiers</SelectItem>
            <SelectItem value="TIER_FREE">TIER_FREE</SelectItem>
            <SelectItem value="TIER_PRO">TIER_PRO</SelectItem>
            <SelectItem value="TIER_ENTERPRISE_HR">TIER_ENTERPRISE_HR</SelectItem>
            <SelectItem value="TIER_RESTRICTED">TIER_RESTRICTED</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-800">
        <table className="w-full text-sm">
          <thead className="bg-slate-950/60 font-mono text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-3 text-left">Entity</th>
              <th className="px-4 py-3 text-left">Type</th>
              <th className="px-4 py-3 text-left">Jurisdiction</th>
              <th className="px-4 py-3 text-center">Score</th>
              <th className="px-4 py-3 text-left">Verdict</th>
              <th className="px-4 py-3 text-left">Tier</th>
              <th className="px-4 py-3 text-left">Status</th>
            </tr>
          </thead>
          <tbody data-testid="case-history-table">
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-slate-500">
                  No cases found.
                </td>
              </tr>
            )}
            {rows.map((s) => (
              <tr key={s.id} className="border-t border-slate-800 transition-colors hover:bg-slate-900/60">
                <td className="px-4 py-3">
                  <Link
                    to={`/cases/${s.id}`}
                    data-testid={`case-row-${s.id}`}
                    className="font-medium text-slate-100 hover:text-blue-400"
                  >
                    {s.entity.name}
                  </Link>
                </td>
                <td className="px-4 py-3 text-slate-400">{s.entity.entity_type}</td>
                <td className="px-4 py-3 text-slate-400">{s.entity.jurisdiction || "—"}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-center gap-2">
                    <RiskDot color={s.risk_color} score={s.risk_score} />
                    <span className="font-mono font-bold tabular-nums text-slate-200">{s.risk_score}</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <VerdictBadge verdict={s.verdict} />
                </td>
                <td className="px-4 py-3">
                  <TierBadge tier={s.assigned_tier} />
                </td>
                <td className="px-4 py-3">
                  <StatusText status={s.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
